
import { supabase } from "@/integrations/supabase/client";
import { BusinessSubmission } from "./types";
import { uploadMedia, createBucketIfNotExists } from "./mediaService";

export const submitBusiness = async (data: BusinessSubmission) => {
  console.log("Submission Process Started", { 
    businessName: data.name, 
    mediaFilesCount: data.mediaFiles?.length || 0,
    approved: data.approved === false ? "explicitly false" : "not set explicitly"
  });
  
  // Check authentication status for debugging
  const { data: authData, error: authError } = await supabase.auth.getSession();
  console.log("Authentication status:", {
    hasSession: !!authData.session,
    isAnonymous: true, // The application uses anonymous inserts
    authError: authError ? JSON.stringify(authError) : null
  });
  
  // Validate required fields
  const requiredFields = ['name', 'description', 'city', 'category'];
  const missingFields = requiredFields.filter(field => 
    !data[field as keyof BusinessSubmission] || 
    (typeof data[field as keyof BusinessSubmission] === 'string' && 
     (data[field as keyof BusinessSubmission] as string).trim() === '')
  );
  
  if (missingFields.length > 0) {
    console.error('Missing required fields', { missingFields });
    throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
  }
  
  // Pre-check storage bucket
  if (data.mediaFiles && data.mediaFiles.length > 0) {
    console.log('Checking storage bucket availability...');
    const bucketCheck = await createBucketIfNotExists();
    
    if (!bucketCheck.success) {
      console.error('Bucket preparation failed', bucketCheck.error);
      throw new Error(
        bucketCheck.error instanceof Error 
          ? bucketCheck.error.message 
          : "Failed to prepare storage for uploads"
      );
    }
  }
  
  try {
    // Insert business record - WITHOUT any user_id field since this is public data
    // Make sure the businesses table DOESN'T have RLS policies requiring user_id
    console.log('Attempting to insert business data...');
    
    // Log the data being inserted for debugging
    const insertData = {
      name: data.name,
      description: data.description,
      address: data.address || null,
      city: data.city,
      state: data.state || null,
      category: data.category,
      tags: data.tags || [],
      website: data.website || null,
      phone: data.phone || null,
      email: data.email || null,
      facebook: data.facebook || null,
      instagram: data.instagram || null,
      tiktok: data.tiktok || null,
      // Explicitly set approved to false to comply with RLS policies
      approved: false
    };
    
    console.log('Business data to insert:', insertData);
    
    // Log RLS debugging information
    console.log('RLS Debug Info:', {
      checkingPolicy: 'Allow public submissions with pending approval',
      approvedFieldValue: insertData.approved,
      approvedFieldType: typeof insertData.approved,
      isExplicitlyFalse: insertData.approved === false
    });
    
    // Let's test a raw SQL query first to see if we can insert
    console.log('Testing raw SQL insert before proceeding...');
    const { data: testData, error: testError } = await supabase.rpc('test_insert_business', {
      business_name: data.name,
      business_description: data.description,
      business_category: data.category
    });
    
    if (testError) {
      console.error('Test insert failed:', testError);
      throw new Error(`RLS test failed: ${testError.message}`);
    } else {
      console.log('Test insert succeeded:', testData);
    }
    
    const { data: responseData, error } = await supabase
      .from('businesses')
      .insert([insertData])
      .select();
    
    if (error) {
      console.error('Business Insertion Error', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code,
        fullError: JSON.stringify(error)
      });
      throw error;
    }
    
    if (!responseData || responseData.length === 0) {
      console.error('No response data from insertion');
      throw new Error("Business insertion failed");
    }
    
    const businessId = responseData[0].id;
    console.log('Business Inserted Successfully', { businessId, responseData });
    
    // Handle media upload
    if (data.mediaFiles && data.mediaFiles.length > 0) {
      console.log(`Uploading ${data.mediaFiles.length} media files for business ID ${businessId}`);
      
      const { mediaUrls, uploadErrors } = await uploadMedia(data.mediaFiles, businessId);
      
      if (uploadErrors.length > 0) {
        console.warn('Some files failed to upload', { uploadErrors });
      }
      
      // Update business with media URLs if any
      if (mediaUrls.length > 0) {
        console.log(`Updating business ${businessId} with ${mediaUrls.length} media URLs`);
        
        const { error: updateError } = await supabase
          .from('businesses')
          .update({ media_urls: mediaUrls })
          .eq('id', businessId);
        
        if (updateError) {
          console.error('Failed to update business with media URLs', {
            message: updateError.message,
            details: updateError.details,
            hint: updateError.hint,
            code: updateError.code,
            fullError: JSON.stringify(updateError)
          });
        } else {
          console.log('Successfully updated business with media URLs');
        }
      }
    }
    
    return responseData;
  } catch (error) {
    console.error('Comprehensive Submission Error', error);
    console.error('Error details:', JSON.stringify(error));
    throw error;
  }
};
